import moment from "moment";
import Address from "./address";
import { ContractType } from "./contract";
import { DeliveryMethod } from "./deliveryMethod";
import { ErpSystem } from "./erpSystem";
import { getTodaysDate } from "common/formatters";

export interface MerchantThin {
    id: string;
    name: string;
    appStoreUrl: string;
    playStoreUrl: string;
    giveUpFee: number;
}

export interface ContractDeliveryMethodMapping {
    contractType: ContractType;
    deliveryMethod: DeliveryMethod;
}

export default interface Merchant extends MerchantThin {
    enableAutoOffer: boolean;
    enablePassOnFill: boolean;
    enableAutoPosition: boolean;
    enableRiskServiceBypass: boolean;
    enableContractImport: boolean;
    isPaymentOptionHidden: boolean;
    isCommentDeletionAllowed: boolean;
    displayCustomerContractIdForSell: boolean;
    displayCustomerContractIdForBuy: boolean;
    makeCustomerContractIdRequired: boolean;
    useCustomerContractIdAsErpId: boolean;
    isDetermineLaterAllowed: boolean;
    hasErpIntegration: boolean;
    displayRegionOnAddOffer: boolean;
    defaultDeferPayInterestRate?: number;
    displayDeliveryTermsWhenCustomerWillBuy: boolean;
    displayDeliveryTermsWhenCustomerWillSell: boolean;
    isPriceAtOpenEnabled: boolean;
    sendNotificationToCustomerDefaultValue: boolean;
    displayContractLocation: boolean;
    marketAutoOfferEnabled: boolean;
    endOfDay: string;
    endOfDayUtc: string;
    mailingAddress: Address;
    phone: string;
    email: string;
    websiteUrl: string;
    contractDeliveryMethodMappings: ContractDeliveryMethodMapping[];
    giveUpFee: number;
    erpSystem?: ErpSystem;
    isInventoryLocationEditingAllowed: boolean;
    useCustomFields: boolean;
    featureFlags: MerchantFeatureFlag[];
    contractTypes: ContractType[];
    notificationSettings: NotificationSettings;
}

export interface MerchantFeatureFlag {
    code: string;
    name: string;
    description: string;
    value: boolean;
}

export interface NotificationSettings {
    displayTotalFees: boolean;
    displayFinalPrice: boolean;
    displaySymbol: boolean;
    displaySymbolMaturity: boolean;
    displayDeliveryPeriod: boolean;
    displayCropYear: boolean;
    displayFreightFee: boolean;
}

export function isFeatureEnabled(merchant: Merchant, feature: MerchantFeature) {
    return merchant.featureFlags.some(m => m.code === feature && m.value);
}

export enum MerchantFeature {
    HedgeRecords = "HedgeRecords",
    ShowCustomerInvitation = "ShowCustomerInvitation",
    ThirdPartyAppIntegration = "ThirdPartyAppIntegration",
    SendToErpToggle = "SendToErpToggle",
    NonAutomatedHedgeRecords = "NonAutomatedHedgeRecords",
    HedgeRecordsAsLandingPage = "HedgeRecordsAsLandingPage",
    ManuallyAddPartnerMappings = "ManuallyAddPartnerMappings",
    ViewPurchaseSalesByPositionGroup = "PurchaseSalesByPositionGroup"
}

export function getStartEndBasedOnMerchant(merchantEndOfDayUtc: string): Date[] {
    if (merchantEndOfDayUtc == null)
        return [moment().add(-1, "days").toDate(), getTodaysDate()];

    const eodInUtc = moment.utc(merchantEndOfDayUtc, "hh:mm:ss"); 
    const utcNow = moment.utc();

    if (utcNow.isBefore(eodInUtc)) {
        eodInUtc.add(-1, "days");
    }
    const startDay = eodInUtc.toDate(); 
    const endDay = eodInUtc.add(1, "days").toDate();
    return [startDay, endDay];
}
